import React, { FormEvent, useState } from 'react';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import MainURL from '../../Admin/MainURL';
import './Inquiry.scss';

const INQUIRY_API_URL = `${MainURL}/api/inquiry`;

const CATEGORY_OPTIONS = [
  { id: 'mobile-app', label: '모바일 앱' },
  { id: 'web-platform', label: '웹 플랫폼' },
  { id: 'responsive-web', label: '일반 반응형 웹' },
  { id: 'etc', label: '기타' },
] as const;

const BUDGET_OPTIONS = [
  { value: '', label: '선택해 주세요' },
  { value: 'under-10m', label: '1천만원 미만' },
  { value: '10m-30m', label: '1천만원 ~ 3천만원' },
  { value: '30m-50m', label: '3천만원 ~ 5천만원' },
  { value: 'over-50m', label: '5천만원 이상' },
  { value: 'undecided', label: '미정 / 상담 후 결정' },
];

const PLANNING_OPTIONS = [
  { value: 'idea', label: '아이디어만 있음' },
  { value: 'requirements', label: '간단한 요구사항 정의됨' },
  { value: 'detailed-plan', label: '스토리보드 및 상세 기획 완료' },
] as const;

type PlanningStatus = (typeof PLANNING_OPTIONS)[number]['value'];

interface ContactFormState {
  name: string;
  company: string;
  email: string;
  phone: string;
  categories: string[];
  budgetSchedule: string;
  planningStatus: PlanningStatus | '';
  message: string;
  privacyAgreed: boolean;
}

type FormErrors = Partial<Record<keyof ContactFormState | 'categories', string>>;

const INITIAL_FORM: ContactFormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  categories: [],
  budgetSchedule: '',
  planningStatus: '',
  message: '',
  privacyAgreed: false,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[0-9+\-()\s]{8,20}$/;

const Inquiry: React.FC = () => {
  const [form, setForm] = useState<ContactFormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  

  const updateField = <K extends keyof ContactFormState>(key: K, value: ContactFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const toggleCategory = (categoryId: string) => {
    setForm((prev) => {
      const exists = prev.categories.includes(categoryId);
      const categories = exists
        ? prev.categories.filter((id) => id !== categoryId)
        : [...prev.categories, categoryId];

      return { ...prev, categories };
    });
    setErrors((prev) => ({ ...prev, categories: undefined }));
  };

  const validate = (): FormErrors => {
    const nextErrors: FormErrors = {};

    if (!form.name.trim()) {
      nextErrors.name = '고객명 / 담당자명을 입력해 주세요.';
    }

    if (!form.email.trim()) {
      nextErrors.email = '이메일 주소를 입력해 주세요.';
    } else if (!EMAIL_PATTERN.test(form.email.trim())) {
      nextErrors.email = '올바른 이메일 형식을 입력해 주세요.';
    }

    if (!form.phone.trim()) {
      nextErrors.phone = '연락처를 입력해 주세요.';
    } else if (!PHONE_PATTERN.test(form.phone.trim())) {
      nextErrors.phone = '올바른 연락처 형식을 입력해 주세요.';
    }

    if (!form.categories.length) {
      nextErrors.categories = '프로젝트 카테고리를 1개 이상 선택해 주세요.';
    }

    if (!form.planningStatus) {
      nextErrors.planningStatus = '현재 기획 상태를 선택해 주세요.';
    }

    if (!form.message.trim()) {
      nextErrors.message = '상세 내용을 입력해 주세요.';
    } else if (form.message.trim().length < 20) {
      nextErrors.message = '상세 내용은 20자 이상 입력해 주세요.';
    }

    if (!form.privacyAgreed) {
      nextErrors.privacyAgreed = '개인정보 수집 및 이용에 동의해 주세요.';
    }

    return nextErrors;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const categoryLabels = form.categories
        .map((id) => CATEGORY_OPTIONS.find((option) => option.id === id)?.label || id)
        .join(', ');

      const budgetLabel =
        BUDGET_OPTIONS.find((option) => option.value === form.budgetSchedule)?.label || '';

      const planningLabel =
        PLANNING_OPTIONS.find((option) => option.value === form.planningStatus)?.label || '';

      const response = await fetch(INQUIRY_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          category: categoryLabels,
          budget: budgetLabel,
          planning_status: planningLabel,
          message: form.message.trim(),
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(errorBody?.message || `Inquiry API responded with ${response.status}`);
      }

      setIsSubmitted(true);
      setForm(INITIAL_FORM);
    } catch (error) {
      console.error('Failed to submit inquiry form:', error);
      setErrors({
        message:
          error instanceof Error && error.message
            ? error.message
            : '접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="contact">
        <Header />
        <section className="contact-success">
          <div className="container contact-success__inner">
            <div className="contact-success__icon" aria-hidden="true" />
            <h1 className="contact-success__title">성공적으로 접수되었습니다.</h1>
            <p className="contact-success__desc">
              소중한 제작 의뢰가 정상적으로 접수되었습니다.
              <br />
              예플랫 담당자가 최대2일(영업일기준) 이내에 입력하신 연락처로 회신드리겠습니다.
            </p>
            <button
              type="button"
              className="contact-success__button"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              홈으로 돌아가기 →
            </button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="contact">
      <Header />

      <section className="contact-hero">
        <div className="contact-hero__gradient" aria-hidden="true" />
        <div className="container contact-hero__inner">
          <span className="contact-hero__eyebrow">Contact</span>
          <h1 className="contact-hero__title">프로젝트를 준비 중이신가요?</h1>
          <p className="contact-hero__desc">
            아이디어 단계부터 구체적인 기획까지, 예플랫 전문가 그룹이 최적의 플랫폼 아키텍처를
            제안해 드립니다. 아래 양식을 작성해 주시면 24시간 이내에 담당자가 연락드리겠습니다.
          </p>
        </div>
      </section>

      <section className="contact-section">
        <div className="container">
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="contact-form__grid">
              <div className="contact-field">
                <label className="contact-field__label" htmlFor="contact-name">
                  고객명 / 담당자명 <span className="contact-field__required">*</span>
                </label>
                <input
                  id="contact-name"
                  className={`contact-field__input ${errors.name ? 'contact-field__input--error' : ''}`}
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="홍길동"
                />
                {errors.name && <p className="contact-field__error">{errors.name}</p>}
              </div>

              <div className="contact-field">
                <label className="contact-field__label" htmlFor="contact-company">
                  회사명 / 단체명
                </label>
                <input
                  id="contact-company"
                  className="contact-field__input"
                  type="text"
                  value={form.company}
                  onChange={(e) => updateField('company', e.target.value)}
                  placeholder="예플랫 주식회사"
                />
              </div>

              <div className="contact-field">
                <label className="contact-field__label" htmlFor="contact-email">
                  이메일 주소 <span className="contact-field__required">*</span>
                </label>
                <input
                  id="contact-email"
                  className={`contact-field__input ${errors.email ? 'contact-field__input--error' : ''}`}
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="contact@example.com"
                />
                {errors.email && <p className="contact-field__error">{errors.email}</p>}
              </div>

              <div className="contact-field">
                <label className="contact-field__label" htmlFor="contact-phone">
                  연락처 <span className="contact-field__required">*</span>
                </label>
                <input
                  id="contact-phone"
                  className={`contact-field__input ${errors.phone ? 'contact-field__input--error' : ''}`}
                  type="tel"
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="010-0000-0000"
                />
                {errors.phone && <p className="contact-field__error">{errors.phone}</p>}
              </div>
            </div>

            <div className="contact-field contact-field--full">
              <span className="contact-field__label">
                프로젝트 카테고리 <span className="contact-field__required">*</span>
              </span>
              <div className="contact-checkbox-group">
                {CATEGORY_OPTIONS.map((option) => {
                  const isChecked = form.categories.includes(option.id);
                  return (
                  <label
                    key={option.id}
                    className={`contact-checkbox${isChecked ? ' contact-checkbox--checked' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleCategory(option.id)}
                    />
                    <span
                      className={`contact-checkbox__box${
                        isChecked ? ' contact-checkbox__box--checked' : ''
                      }`}
                      aria-hidden="true"
                    >
                      {isChecked && (
                        <svg
                          className="contact-checkbox__icon"
                          viewBox="0 0 12 10"
                          aria-hidden="true"
                        >
                          <path
                            d="M1 5.5L4.5 9L11 1"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="contact-checkbox__label">{option.label}</span>
                  </label>
                  );
                })}
              </div>
              {errors.categories && <p className="contact-field__error">{errors.categories}</p>}
            </div>

            <div className="contact-form__grid">
              <div className="contact-field">
                <label className="contact-field__label" htmlFor="contact-budget">
                  예상 예산 / 일정
                </label>
                <select
                  id="contact-budget"
                  className="contact-field__input contact-field__select"
                  value={form.budgetSchedule}
                  onChange={(e) => updateField('budgetSchedule', e.target.value)}
                >
                  {BUDGET_OPTIONS.map((option) => (
                    <option key={option.value || 'empty'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="contact-field">
                <span className="contact-field__label">
                  현재 기획 상태 <span className="contact-field__required">*</span>
                </span>
                <div className="contact-radio-group">
                  {PLANNING_OPTIONS.map((option) => {
                    const isSelected = form.planningStatus === option.value;
                    return (
                    <label
                      key={option.value}
                      className={`contact-radio${isSelected ? ' contact-radio--checked' : ''}`}
                    >
                      <input
                        type="radio"
                        name="planningStatus"
                        value={option.value}
                        checked={isSelected}
                        onChange={() => updateField('planningStatus', option.value)}
                      />
                      <span
                        className={`contact-radio__dot${
                          isSelected ? ' contact-radio__dot--checked' : ''
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && <span className="contact-radio__inner" />}
                      </span>
                      <span className="contact-radio__label">{option.label}</span>
                    </label>
                    );
                  })}
                </div>
                {errors.planningStatus && (
                  <p className="contact-field__error">{errors.planningStatus}</p>
                )}
              </div>
            </div>

            <div className="contact-field contact-field--full">
              <label className="contact-field__label" htmlFor="contact-message">
                상세 내용 <span className="contact-field__required">*</span>
              </label>
              <textarea
                id="contact-message"
                className={`contact-field__textarea ${errors.message ? 'contact-field__input--error' : ''}`}
                value={form.message}
                onChange={(e) => updateField('message', e.target.value)}
                placeholder="프로젝트 목표, 주요 기능, 참고 서비스, 희망 일정 등을 자유롭게 작성해 주세요. (20자 이상)"
                rows={7}
              />
              <p className="contact-field__hint">{form.message.trim().length} / 최소 20자</p>
              {errors.message && <p className="contact-field__error">{errors.message}</p>}
            </div>

            <div className="contact-field contact-field--full">
              <div className="contact-field__privacy-row">
                <label
                  className={`contact-checkbox contact-checkbox--agree${
                    form.privacyAgreed ? ' contact-checkbox--checked' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.privacyAgreed}
                    onChange={(e) => updateField('privacyAgreed', e.target.checked)}
                  />
                  <span
                    className={`contact-checkbox__box${
                      form.privacyAgreed ? ' contact-checkbox__box--checked' : ''
                    }`}
                    aria-hidden="true"
                  >
                    {form.privacyAgreed && (
                      <svg
                        className="contact-checkbox__icon"
                        viewBox="0 0 12 10"
                        aria-hidden="true"
                      >
                        <path
                          d="M1 5.5L4.5 9L11 1"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="contact-checkbox__label">
                    개인정보 수집 및 이용에 동의합니다.{' '}
                    <span className="contact-field__required">*</span>
                  </span>
                </label>
                <a
                  className="contact-field__privacy-link"
                  href="http://www.yeplat.com/personalinfo.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  개인정보처리방침 자세히보기
                </a>
              </div>
              <p className="contact-field__privacy">
                수집 항목: 이름, 이메일, 연락처, 회사명, 문의 내용 · 이용 목적: 제작 의뢰 상담 및
                회신 · 보유 기간: 상담 완료 후 1년
              </p>
              {errors.privacyAgreed && (
                <p className="contact-field__error">{errors.privacyAgreed}</p>
              )}
            </div>

            <button type="submit" className="contact-form__submit" disabled={isSubmitting}>
              {isSubmitting ? '접수 중...' : '제작의뢰 신청하기 →'}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Inquiry;
